<script setup lang="ts">
import { computed, inject } from 'vue'
import { useTheme } from 'vuetify'

import PolicyWidget from '@kidzonet/dashboard-overview-policy-widget'
import { $busEmit } from '@kidzonet/inject-bus-emit'
import { getPolicyDescription, getPolicyName } from '@kidzonet/use-policy'

interface PolicyItemProps {
    name?: string
    header?: string
    isActive?: boolean
    subheader?: string
    hideTooltip?: boolean
}

const props = withDefaults(defineProps<PolicyItemProps>(), {
    name: '',
    hideTooltip: false,
})

const theme = useTheme()

const classList = computed(() => {
    const result = ['policy-item']
    if (props.isActive) {
        result.push('policy-item--active')
    }
    if (theme.global.current.value.dark) {
        result.push('policy-item--dark')
    }
    return result
})

const tooltipConfig = computed(() => {
    if (props.hideTooltip) {
        return {
            openOnHover: false,
            persistent: false,
        }
    }
    const name = props.subheader || getPolicyDescription(props.name)
    return {
        text: name,
        location: 'bottom',
        maxWidth: '400',
        openOnHover: false,
        persistent: false,
        closeDelay: 300,
        openOnClick: true,
        id: `id-${props.subheader || props.name}-tooltip`,
        ariaLabel: name,
    }
})

const subheaderInPolicyDesription = computed(() => {
    if (props.hideTooltip) {
        return props.subheader || getPolicyDescription(props.name)
    }
    return ''
})

const policyName = computed(() => props.name ? getPolicyName(props.name) : props.header)

const busEmit = inject($busEmit)

const showAriaTooltip = () => {
    if (!busEmit) {
        return
    }
    // eslint-disable-next-line ts/ban-ts-comment
    // @ts-ignore
    busEmit('pronounce', {
        // eslint-disable-next-line ts/ban-ts-comment
        // @ts-ignore
        message: `${policyName.value} ${props.isActive ? 'deactivated' : 'activated'}`,
        timeout: 500,
    })
}
</script>

<template>
    <PolicyWidget
        :header="policyName"
        :subheader="subheaderInPolicyDesription"
        :class="classList"
        v-ripple
        :aria-label="policyName"
        v-tooltip="tooltipConfig"
        @keyup.enter="showAriaTooltip"
        @click="showAriaTooltip"
    >
        <template #icon>
            <slot name="icon" />
        </template>
        <template #action>
            <slot />
        </template>
        <slot name="bottom" />
    </PolicyWidget>
</template>

<style>
    .policy-item {
        --active-border-color: #C0BCE5;
        --active-background-color: #FCFBFE;

        cursor: pointer;
        border: 1px solid #E4E4E4;

        &--dark {
            --active-border-color: inherit;
            --active-background-color: #000;
        }

        &:hover, &--active {
            border-color: var(--active-border-color);
            background-color: var(--active-background-color) !important;
        }

        @media(max-width: 1100px) {
            block-size: auto;
        }
    }

    .policy-item__icon-wrapper {
        display: flex;
        align-items: center;
        justify-content: center;

        min-inline-size: 48px;
        min-block-size: 48px;
        border-radius: 12px;
    }
</style>
